import React, { useState, useEffect, useCallback } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import IconButton from '@material-ui/core/IconButton';
import ListItemText from '@material-ui/core/ListItemText';
import RemoveIcon from '@material-ui/icons/RemoveCircleOutline';
import api from "../../api";
import { inject } from "mobx-react";
import { useFeedback } from '../feedback/Service';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';

const UserRecordingsDialog = ({ userId, open, handleClose, allRecordings, allProducts }) => {
  const [recordings, setRecordings] = useState([]);
  const [selectedRecording, setSelectedRecording] = useState(0);
  const [sendEmail, setSendEmail] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const feedback = useFeedback();

  const getUserProducts = useCallback(() => {
    if (!userId) return;
    setIsFetching(true);
    Promise.all([api.getUserRecordings(userId), api.getUserProducts(userId)]).then(([recordings, products]) => {
      const userProducts = allProducts.filter(x => products.map(c => c.productId).includes(x.id));
      const productRecordingIds = userProducts.map(x => x.recordings || []).flat();
      const productRecordings = allRecordings.filter(x => productRecordingIds.includes(x.id)).map(x => ({ ...x, product: userProducts.find(p => p.recordings.includes(x.id)) }));
      setRecordings([...recordings.map(x => ({ ...x, id: x.recordingId })), ...productRecordings]);
      console.log("recordings fetch result", recordings, productRecordings);
      setIsFetching(false);
    }).catch(error => {
      console.warn("Error getting user recordings", error.response?.data?.message || error);
      setIsFetching(false);
    });
  }, [userId, allRecordings, allProducts]);

  const handleAdd = async () => {
    if (selectedRecording === 0) return;
    try {
      setIsFetching(true);
      await api.addUserRecording(userId, { sendEmail, recordingId: selectedRecording });
      getUserProducts();
    }
    catch (error) {
      console.error("Error adding recording", error.response?.data?.message || error)
      setIsFetching(false);
      feedback.snackbar({ text: error.response?.data?.message || "Error adding recording", type: "error" });
      return;
    }
    setSendEmail(false);
    setSelectedRecording(0);
    feedback.snackbar({ text: "Access granted", type: "success" });
  }

  const handleDelete = recordingId => async () => {
    if (!await feedback.confirm({ title: "Remove access to this recording?" }))
      return;
    try {
      setIsFetching(true);
      await api.removeUserRecording(recordingId, userId);
      getUserProducts();
    }
    catch (error) {
      console.error("Error removing access", error.response?.data?.message || error)
      setIsFetching(false);
      feedback.snackbar({ text: error.response?.data?.message || "Error removing recording", type: "error" });
      return;
    }
    feedback.snackbar({ text: "Access removed", type: "success" });
  }

  useEffect(() => {
    getUserProducts();
  }, [getUserProducts]);

  const availableProducts = allRecordings.filter(x => !recordings.map(c => c.id).includes(x.id));
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      disableScrollLock
    >
      {isFetching ? <LinearProgress variant="query" /> : <div style={{ height: 4 }}></div>}
      <DialogTitle id="alert-dialog-title">{"User's Recordings"}</DialogTitle>
      <DialogContent>
        {availableProducts.length > 0 &&
          <>
            <Select
              style={{ minWidth: 150 }}
              labelId="event-label"
              id="event-select"
              value={selectedRecording}
              onChange={e => setSelectedRecording(e.target.value)}
            >
              <MenuItem value={0}>Select recording</MenuItem>
              {availableProducts.map(recording => (<MenuItem value={recording.id} key={recording.id}>{recording.name}</MenuItem>))}
            </Select>
            <FormControlLabel
              style={{ marginLeft: 5 }}
              control={
                <Checkbox
                  checked={sendEmail}
                  onChange={e => setSendEmail(e.target.checked)}
                  color="primary"
                />
              }
              label={"Send Email"}
            />
            <Button color="primary" disabled={selectedRecording === 0} onClick={handleAdd}>Add</Button>
            <br />
            <br />
          </>
        }
        {recordings.length > 0 ?
          <List component="nav" dense>
            {recordings.map((recording, i) => (
              <ListItem key={(recording.product && "p_") + recording.id}>
                <ListItemText primary={recording.name} secondary={recording.product ? "From product: " + recording.product.title : undefined} />
                {!recording.product &&
                  <IconButton onClick={handleDelete(recording.id)}>
                    <RemoveIcon />
                  </IconButton>
                }
              </ListItem>
            ))}
          </List>
          :
          <DialogContentText>
            No recordings available for this user.
          </DialogContentText>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" autoFocus>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default inject(({ store }, props) => ({
  allRecordings: store.recordings, allProducts: store.products
}))(UserRecordingsDialog);